// The console tour (spec/13 §2): a few stops around the sidebar — Setup, Floor, Sell, Stations, Library, Reports,
// Settings — one plain sentence each, on the kit's Tour. An owner or manager sees it once, on their first visit to a
// venue; "Take the tour" in the staff menu plays it again. A stop whose section the member's role does not list (or
// that is not there yet, Setup once every venue is open) is left out.
//
// **Shown once per person, remembered in this browser.** There is no per-person preferences store on the server yet
// (notification preferences are the only per-person settings it keeps), so "seen" is kept here, per signed-in person:
// a new browser, or a cleared one, shows it once more. It is a welcome, never state anything relies on, so a store that
// cannot be written simply shows it again next time.

import { useTranslation } from '@clavian/i18n/react';
import { Tour, type TourStop } from '@clavian/ui-web';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useSession } from '../api/queries';

/** The stops, in order: each a sidebar section, by its id (`shell/sections.ts`). */
export const TOUR_STOPS = ['setup', 'floor', 'sell', 'stations', 'library', 'reports', 'settings'] as const;

const seenKey = (userId: string) => `clavian:tour:console:${userId}`;

function readSeen(userId: string): boolean {
  try {
    return window.localStorage.getItem(seenKey(userId)) !== null;
  } catch {
    return false;
  }
}

function writeSeen(userId: string): void {
  try {
    window.localStorage.setItem(seenKey(userId), new Date().toISOString());
  } catch {
    // A private window or blocked storage: the tour comes back next visit, which is harmless.
  }
}

export interface ConsoleTour {
  /** Play the tour now (the staff menu's "Take the tour"). */
  readonly start: () => void;
  /** The tour itself, to render inside the shell. */
  readonly element: ReactNode;
}

/** Is a dialog open already (the shift prompt, a Setup dialog)? The tour waits for it rather than stacking on top. */
function dialogOpen(): boolean {
  return document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]') !== null;
}

/** Is the sidebar drawn? Folded away on a phone, there is nothing to point at: no tour, and not marked seen. */
function sidebarShown(): boolean {
  const first = document.querySelector('.console-sidebar [data-tour]');
  return first !== null && first.getClientRects().length > 0;
}

/**
 * A shell's tour: played once for a newcomer who runs a venue (`autoStart`), and on request after that (`start`). The
 * first time waits until no dialog is open and the sidebar is drawn, then marks it seen however it ends. Called by the
 * layout before anything else it draws, so the staff menu it builds can offer "Take the tour".
 */
export function useConsoleTour(autoStart: boolean): ConsoleTour {
  const { t } = useTranslation('console');
  const userId = useSession().data?.user?.id;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!autoStart || userId === undefined || readSeen(userId)) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      // Up to a minute: a newcomer may sit in the shift prompt for a while.
      if (tries > 60) window.clearInterval(timer);
      else if (!dialogOpen() && sidebarShown()) {
        window.clearInterval(timer);
        setOpen(true);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [autoStart, userId]);

  const stops = useMemo<readonly TourStop[]>(
    () =>
      TOUR_STOPS.map((id) => ({
        id,
        target: `.console-sidebar [data-tour="${id}"]`,
        title: t(`tour.stops.${id}.title`),
        body: t(`tour.stops.${id}.body`),
        side: 'right' as const,
      })),
    [t],
  );

  const close = useCallback(() => {
    setOpen(false);
    if (userId !== undefined) writeSeen(userId);
  }, [userId]);

  const start = useCallback(() => setOpen(true), []);
  return { start, element: <Tour label={t('tour.label')} stops={stops} open={open} onClose={close} /> };
}
